import type {TrimmedAudioFields} from '@Libraries/audio-player/audio-player.library';
import {getTrimmedEndTime, getTrimmedStartTime} from '@Libraries/audio-player/audio-player.library';
import {MIN_ITEM_DURATION} from '@PosterWhiteboard/items/item/item.types';
import {isVideoLoopingEnabledForItem} from '@PosterWhiteboard/items/video-item/video-item.types';
import {quantizeTimeSeconds} from '@Utils/math.util';

/**
 * Pure math for timeline group gestures — the single source of truth shared by the
 * whiteboard commit path (`Page.moveTimelineGroup` / `Page.resizeTimelineGroup`) and
 * the UI's live co-preview (`useGroupModifyPreview`), so what a group gesture
 * previews is exactly what it commits. Lives whiteboard-side because the domain
 * layer cannot import from `@Components`.
 */

export interface TimeSpan {
  startTime: number;
  endTime: number;
}

/** Which edge of a timeline bar a resize gesture grabbed. */
export type TimelineResizeHandle = 'left' | 'right';

/**
 * How far one bar's edges may travel during a timeline resize. `minStartTime` /
 * `maxEndTime` bound the GROW direction: `maxEndTime: null` means the right edge is
 * unbounded (plain items; looping videos), while trimmed media (audio clips;
 * non-looping videos) bound both edges by the source content still available outside
 * the current trim. `maxStartTime` / `minEndTime` bound the SHRINK direction beyond
 * the generic MIN_ITEM_DURATION clamp — `null` means only that generic clamp applies;
 * slideshows use them because their edges can only travel what the first/last slide's
 * duration limits absorb.
 */
export interface TimelineResizeBounds {
  minStartTime: number;
  maxStartTime: number | null;
  maxEndTime: number | null;
  minEndTime: number | null;
}

export const UNBOUNDED_TIMELINE_RESIZE: TimelineResizeBounds = {minStartTime: 0, maxStartTime: null, maxEndTime: null, minEndTime: null};

/**
 * The shared delta a group gesture actually applies on one axis (time, or sub-row):
 * the raw delta held back so the earliest/topmost member stops at 0 — the group
 * blocks at the timeline boundary instead of compressing its relative offsets.
 */
export const clampGroupDeltaToFloor = (rawDelta: number, positions: number[]): number => {
  let delta = rawDelta;
  for (const position of positions) {
    delta = Math.max(delta, -position);
  }
  // A member at position 0 clamps a negative delta to -0; canonicalize so callers and tests see one zero.
  return delta === 0 ? 0 : delta;
};

/**
 * The shared row delta a group gesture applies within ONE track: floor-clamped so the
 * topmost member stops at row 0 and ceiling-clamped so the bottom-most member stops at
 * the track's last existing row. A group reorders within the rows the track already
 * has — only a single-bar drag can mint a new row. Rows are track-local coordinates,
 * so each track clamps and applies its own delta; cross-track members never row-shift.
 */
export const clampGroupRowDelta = (rawDelta: number, memberRows: number[], maxRow: number): number => {
  let delta = clampGroupDeltaToFloor(rawDelta, memberRows);
  for (const row of memberRows) {
    delta = Math.min(delta, maxRow - row);
  }
  return delta === 0 ? 0 : delta;
};

interface TrimmedMediaData {
  trimStartTime: number;
  trimEndTime: number;
  sourceDuration: number;
  speed: number;
}

/**
 * Resize bounds for a bar backed by trimmed source media. Looping media repeats to
 * fill whatever span the bar takes, so both edges are source-unbounded (the left
 * still floors at page time 0). Non-looping media is trim-bounded: the left edge
 * may travel as far as the un-played lead-in allows (at playback speed), the right
 * edge as far as the un-played tail allows.
 */
export const getTrimmedMediaResizeBounds = (span: TimeSpan, media: TrimmedMediaData, loopsToFillSpan: boolean): TimelineResizeBounds => {
  return {
    minStartTime: loopsToFillSpan ? 0 : Math.max(0, span.startTime - media.trimStartTime / media.speed),
    maxStartTime: null,
    maxEndTime: loopsToFillSpan ? null : span.endTime + (media.sourceDuration - media.trimEndTime) / media.speed,
    minEndTime: null,
  };
};

interface VideoTrimFields {
  startTime: number;
  endTime: number;
  duration: number;
  speed: number;
  isBackground: boolean;
}

/**
 * Video resize bounds from the fields VideoItem and VideoItemObject share, so the
 * class commit and the UI's gesture snapshot compute from one definition: a looping
 * video's edges are span-only (unbounded); a non-looping video's edges are bounded
 * by the un-played source outside the current trim.
 */
export const getVideoTimelineResizeBounds = (span: TimeSpan, video: VideoTrimFields): TimelineResizeBounds => {
  return getTrimmedMediaResizeBounds(span, {trimStartTime: video.startTime, trimEndTime: video.endTime, sourceDuration: video.duration, speed: video.speed}, isVideoLoopingEnabledForItem(video));
};

/**
 * The source-trim end a video bar takes when it converts into a background.
 * Backgrounds never loop: a looping bar's span can cover several playback passes,
 * so the trim window clamps to one pass over the bar's span. A bar shorter than
 * one pass trims the un-played tail off instead, so the converted background's
 * derived span still matches the bar the user dropped.
 */
export const getVideoBackgroundConversionTrimEndTime = (spanDuration: number, video: Pick<VideoTrimFields, 'startTime' | 'endTime' | 'speed'>): number => {
  const trimWindow = Math.min(spanDuration * video.speed, video.endTime - video.startTime);
  return quantizeTimeSeconds(video.startTime + Math.max(MIN_ITEM_DURATION * video.speed, trimWindow));
};

/**
 * Audio resize bounds from the fields AudioPlayer and AudioPlayerObject share, so the
 * class commit and the UI's gesture snapshot compute from one definition: both edges
 * are bounded by the source outside the current trim.
 *
 * Reads the effective window, never the raw trim fields: an untrimmed clip stores
 * `{isTrimmed: false, startTime: 0, endTime: 0}`, whose raw end would put the whole
 * source in the un-played tail and let the right edge stretch to twice its length.
 */
export const getAudioTimelineResizeBounds = (span: TimeSpan, audioPlayer: TrimmedAudioFields): TimelineResizeBounds => {
  return getTrimmedMediaResizeBounds(
    span,
    {trimStartTime: getTrimmedStartTime(audioPlayer), trimEndTime: getTrimmedEndTime(audioPlayer), sourceDuration: audioPlayer.originalDuration, speed: audioPlayer.speed},
    false
  );
};

/**
 * The span one bar takes when a group resize applies `delta` to `handle`, clamped
 * per bar: only the grabbed edge moves, a bar never shrinks below
 * MIN_ITEM_DURATION, and each edge stops at its own bounds — bars that can't absorb
 * the full delta stop at their own limit instead of blocking the group.
 */
export const getGroupResizedSpan = (span: TimeSpan, bounds: TimelineResizeBounds, handle: TimelineResizeHandle, delta: number): TimeSpan => {
  if (handle === 'left') {
    const maxStartTime = Math.min(bounds.maxStartTime ?? Number.POSITIVE_INFINITY, span.endTime - MIN_ITEM_DURATION);
    return {startTime: Math.min(Math.max(bounds.minStartTime, span.startTime + delta), maxStartTime), endTime: span.endTime};
  }
  const maxEndTime = bounds.maxEndTime ?? Number.POSITIVE_INFINITY;
  const minEndTime = Math.max(bounds.minEndTime ?? 0, span.startTime + MIN_ITEM_DURATION);
  return {startTime: span.startTime, endTime: Math.max(Math.min(maxEndTime, span.endTime + delta), minEndTime)};
};
